import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useSpring } from "framer-motion";

/**
 * CinematicChrome — mount once at App-level.
 * Provides site-wide:
 *  - Custom cursor (cream dot + champagne gold trailing ring, hides on touch, respects reduced-motion)
 *  - Scroll-progress ribbon at the very top of the viewport
 *  - Subtle SVG grain overlay for cinematic tooth
 *
 * Nothing here is per-page; per-page motion (letter reveals, magnetic buttons)
 * still lives inside each page.
 */
export default function CinematicChrome() {
  const reduced = useReducedMotion();
  const dot = useRef(null);
  const ring = useRef(null);
  const [hovering, setHovering] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 20 });

  useEffect(() => {
    if (reduced) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    let raf = 0;
    let x = window.innerWidth / 2,
      y = window.innerHeight / 2;
    let rx = x,
      ry = y;

    const move = (e) => {
      x = e.clientX;
      y = e.clientY;
      if (dot.current)
        dot.current.style.transform = `translate3d(${x - 4}px, ${y - 4}px, 0)`;
    };
    const loop = () => {
      rx += (x - rx) * 0.16;
      ry += (y - ry) * 0.16;
      if (ring.current)
        ring.current.style.transform = `translate3d(${rx - 18}px, ${ry - 18}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    const enter = (e) => {
      if (e.target?.closest?.("a,button,[data-cursor='hover']")) setHovering(true);
    };
    const leave = (e) => {
      if (e.target?.closest?.("a,button,[data-cursor='hover']")) setHovering(false);
    };

    window.addEventListener("mousemove", move);
    document.addEventListener("mouseover", enter, true);
    document.addEventListener("mouseout", leave, true);
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", enter, true);
      document.removeEventListener("mouseout", leave, true);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <>
      {/* Scroll-progress ribbon — champagne accent, fixed top */}
      <motion.div
        aria-hidden
        initial={{ scaleX: 0 }}
        style={{ scaleX, transformOrigin: "left" }}
        className="fixed top-0 left-0 right-0 h-[2px] z-[60] pointer-events-none"
      >
        <div className="h-full w-full" style={{ background: "#C9A961" }} />
      </motion.div>

      {/* Grain overlay — subtle SVG noise, site-wide */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.04] mix-blend-overlay z-[55]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.96 0 0 0 0 0.94 0 0 0 0 0.91 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Custom cursor — hidden on touch + reduced-motion */}
      {!reduced && (
        <>
          <div
            ref={dot}
            aria-hidden
            className="pointer-events-none fixed left-0 top-0 z-[9999] h-2 w-2 rounded-full bg-[#F5F1E8] mix-blend-difference hidden md:block"
          />
          <div
            ref={ring}
            aria-hidden
            className="pointer-events-none fixed left-0 top-0 z-[9998] rounded-full border hidden md:block"
            style={{
              transition:
                "width .25s ease, height .25s ease, border-color .25s ease",
              width: hovering ? 56 : 36,
              height: hovering ? 56 : 36,
              borderColor: hovering ? "rgba(201,169,97,1)" : "rgba(201,169,97,0.5)",
            }}
          />
        </>
      )}
    </>
  );
}
